import React, { useCallback, useEffect, useRef, useState } from 'react';

import * as cls from './hero.module.css';

export default function Hero({
  children,
  className,
  img,
  items,
  timeout = 6000,
}) {
  const [active, setActive] = useState(0);
  const hover = useRef(false);
  const timer = useRef();

  const isTouchScreen = useRef(
    typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches,
  );
  const heroRef = useRef(null);
  const [isTouch, setIsTouch] = useState(false);
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 });
  const [touchOffset, setTouchOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);

  const handleGoTo = useCallback(
    (i) => {
      // const next = active < items.length - 1 ? active + 1 : 0;
      if (i === active) return;
      setActive(i);
    },
    [active],
  );

  const clearTimer = useCallback(() => {
    clearTimeout(timer.current);
  }, []);

  const handleMouseEnter = () => {
    hover.current = true;
    clearTimer();
  };

  const handleMouseLeave = () => {
    hover.current = false;
    updateTimer();
  };

  // Update setTimer to use the latest handleNext
  const updateTimer = useCallback(() => {
    if (!hover.current && !isSwiping && !isTouch) {
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        const next = active < items.length - 1 ? active + 1 : 0;
        setActive(next);
      }, timeout);
    }
  }, [active, items.length, timeout, isSwiping, isTouch]);

  const handleTouchStart = useCallback(
    (e) => {
      if (!isTouchScreen.current || items.length < 2) return;

      const touch = e.touches[0];
      setTouchStart({ x: touch.clientX, y: touch.clientY });
      setIsTouch(true);
      setIsSwiping(false);
      setTouchOffset(0);
      clearTimer();
    },
    [items.length, clearTimer, isTouchScreen],
  );

  const handleTouchMove = useCallback(
    (e) => {
      if (!isTouch || !isTouchScreen.current || items.length < 2) return;

      const touch = e.touches[0];
      const deltaX = touch.clientX - touchStart.x;
      const deltaY = touch.clientY - touchStart.y;

      // Only handle horizontal swipes
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
        e.preventDefault();
        setIsSwiping(true);
        // Limit the offset to prevent excessive movement
        const maxOffset = heroRef.current
          ? heroRef.current.offsetWidth * 0.3
          : 100;
        const limitedDelta = Math.max(-maxOffset, Math.min(maxOffset, deltaX));
        setTouchOffset(limitedDelta);
      }
    },
    [isTouch, touchStart, items.length, isTouchScreen],
  );

  const handleTouchEnd = useCallback(() => {
    if (!isTouch || !isTouchScreen.current || items.length < 2) {
      setIsTouch(false);
      setIsSwiping(false);
      setTouchOffset(0);
      return;
    }

    const threshold = heroRef.current ? heroRef.current.offsetWidth * 0.15 : 50; // 15% of width

    if (Math.abs(touchOffset) > threshold) {
      if (touchOffset > 0) {
        // Swipe right - go to previous
        const prev = active > 0 ? active - 1 : items.length - 1;
        setActive(prev);
      } else {
        // Swipe left - go to next
        const next = active < items.length - 1 ? active + 1 : 0;
        setActive(next);
      }
    }

    setIsTouch(false);
    setIsSwiping(false);
    setTouchOffset(0);

    // Restart timer after a short delay to allow animation to complete
    setTimeout(() => {
      updateTimer();
    }, 100);
  }, [isTouch, touchOffset, active, items.length, updateTimer, isTouchScreen]);

  useEffect(() => {
    if (items.length < 2) return;
    updateTimer();
    return () => clearTimer();
  }, [items.length, updateTimer, clearTimer]);

  return (
    <div
      className={`${cls.hero} ${items.length > 1 ? cls.multiple : ''} ${className || ''}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      ref={heroRef}
    >
      {img && <img alt="" src={img} />}

      {items.map((item, i) => {
        let transform = '';
        const isActive = active === i;
        const isPrev =
          active - 1 === i || (active === 0 && i === items.length - 1);
        const isNext =
          active + 1 === i || (active === items.length - 1 && i === 0);

        // Determine which slides should be visible and their positioning
        let wrapperClasses = cls.wrapper;

        if (isSwiping) {
          // During swiping, show current slide and the one being swiped toward
          const showDuringSwipe =
            isActive ||
            (touchOffset > 0 && isPrev) || // Swiping right, show previous
            (touchOffset < 0 && isNext); // Swiping left, show next

          if (showDuringSwipe) {
            wrapperClasses += ` ${cls.swiping}`;
            transform = `translateX(${touchOffset}px)`;

            // Apply proper CSS positioning during swiping
            if (!isActive && touchOffset > 0 && isPrev) {
              // When swiping right, previous slide needs to be positioned left
              wrapperClasses += ` ${cls['wrapper-prev']}`;
            }
          }
        } else {
          // For transitions and initial positioning
          if (!isActive) {
            // Always position previous slide on the left so it can slide in from left when needed
            if (isPrev) {
              wrapperClasses += ` ${cls['wrapper-prev']}`;
            }
            // Next and other slides stay at default right position (left: 100%)
          }
        }

        // Always add active class for active slides
        if (isActive) {
          wrapperClasses += ` ${cls.active}`;
        }

        return (
          <div
            className={wrapperClasses}
            key={i}
            style={{
              transform: transform || undefined,
            }}
          >
            <div className={cls.container}>
              <div>
                {item.title &&
                  (i === 0 ? <h1>{item.title}</h1> : <h2>{item.title}</h2>)}
                {item.text && <p>{item.text}</p>}
                {item.caption && <span>{item.caption}</span>}
              </div>
              {item.action ? item.action : null}
            </div>
            {item.img && (
              <>
                <picture>
                  {item.imgMobile ? (
                    <source
                      media={`(max-width: ${item.imgMobileSize}px)`}
                      srcSet={item.imgMobile}
                    />
                  ) : null}
                  <img src={item.img} alt={item.imgAlt || ''} />
                </picture>
                {item.overlay && <div className={cls.overlay} />}
              </>
            )}
          </div>
        );
      })}

      {children}

      {items.length > 1 && (
        <div className={cls['circle__wrapper']}>
          {items.map((_, i) => (
            <button
              className={`${cls.circle} ${i === active ? cls.active : ''}`}
              key={i}
              onClick={() => handleGoTo(i)}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            ></button>
          ))}
        </div>
      )}
      {/*items.length > 1 && (
        <>
          <button
            className={cls.prev}
            onClick={handlePrev}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <img src={`${IMG_PATH}/hero-arrow.svg`} alt="previous banner" />
          </button>
          <button
            className={cls.next}
            onClick={handleNext}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <img src={`${IMG_PATH}/hero-arrow.svg`} alt="next banner" />
          </button>
        </>
      )*/}
    </div>
  );
}
